<script setup lang="ts">
import DivTable from '@/components/vuetify-custom-components/DivTable.vue'
import { can } from '@layouts/plugins/casl'
import { useSnackbarStore } from '@/store/snackbar'
import { formatDecimalAndNumberWithComma } from '@/helpers/numFormats'

import type { TableHeader, TableSorting, TableOptions } from '@/models/general/table'
import type { RouteLocationRaw } from 'vue-router'
import {
  downloadAllFilesUsingLinks,
  downloadFromS3,
} from '@/helpers/utils/downloadAllFilesUsingLinks'
import type { DownloadLinkInput } from '@/helpers/utils/downloadAllFilesUsingLinks'
import { isTextTruncated } from '@/helpers/utils/textTruncationCheck'
import { usePriority } from '@/helpers/composables/usePriority'

interface IProps {
  lineItems?: any[],
  lineItemsCount?: number,
  lineItemsLoading?: boolean,
  selectedLineItemIds?: number[],
  lineItemTableOptions?: TableOptions,
  lineItemHeaders?: TableHeader[],
  selectedHistoryLineItemId?: number | null,
  showLineItemStatusHistoryDialog?: boolean,
  selectedChatLineItemId?: number | number[] | null,
  showGlobalChatDialog?: boolean,
  isShowItemReviewDialogInsteadOfReviewPage?: boolean,
  selectedLineItemForReviewDialog?: {
    campaign_id: string | number | null,
    work_order_id: string | number | null,
    id: string | number | null
  },
  showLineItemReviewDialog?: boolean,
  showTableColumnSettingsDialog?: boolean,
  showActiveProjectsFilter?: boolean,
  isActiveProjectsFilter?: boolean,
  isOnHoldFilter?: boolean,
  isOnHoldOptions?: boolean[],
  isReleasedFilter?: true | null,
  hiddenVisibilityFilter?: boolean,
  rejectedItemsOnlyFilter?: true | null,
  isMultipleApprovalDialog?: boolean,
  campaignType?: number | null,
  hideTableColumnSettings?: boolean,
  getLineItemReviewLink: (lineItemId: number) => RouteLocationRaw,
}

const props = withDefaults(defineProps<IProps>(), {
  lineItems: (() => ([])),
  lineItemsCount: 0,
  lineItemsLoading: false,
  selectedLineItemIds: (() => ([])),
  lineItemTableOptions: (() => ({
    itemsPerPage: 20,
    page: 1,
    sortBy: []
  })),
  lineItemHeaders: (() => ([])),
  selectedHistoryLineItemId: null,
  showLineItemStatusHistoryDialog: false,
  selectedChatLineItemId: null,
  showGlobalChatDialog: false,
  hiddenVisibilityFilter: false,
  isShowItemReviewDialogInsteadOfReviewPage: true,
  selectedLineItemForReviewDialog: (() => ({
    campaign_id: null,
    work_order_id: null,
    id: null
  })),
  showLineItemReviewDialog: false,
  isMultipleApprovalDialog: false,
  showTableColumnSettingsDialog: false,
  showActiveProjectsFilter: false,
  isActiveProjectsFilter: true,
  isOnHoldFilter: false,
  isOnHoldOptions: (() => ([true, false])),
  isReleasedFilter: null,
  hiddenVisibilityFilter: false,
  rejectedItemsOnlyFilter: null,
  campaignType: null,
  hideTableColumnSettings: false,
  getLineItemReviewLink: (lineItemId: number) => ({
    path: `/projects/branding/global-search-line-items/${lineItemId}`,
  }),
})

interface Emit {
  (e: 'update:lineItems', value: any[]): void
  (e: 'update:selectedLineItemIds', value: number[]): void
  (e: 'update:lineItemTableOptions', value: TableOptions): void
  (e: 'update:selectedHistoryLineItemId', value: number): void
  (e: 'update:showLineItemStatusHistoryDialog', value: boolean): void
  (e: 'update:selectedChatLineItemId', value: number | number[]): void
  (e: 'update:showGlobalChatDialog', value: boolean): void
  (e: 'update:selectedLineItemForReviewDialog', value: {
    campaign_id: string | number | null,
    work_order_id: string | number | null,
    id: string | number | null
  }): void
  (e: 'update:showLineItemReviewDialog', value: boolean): void
  (e: 'update:showTableColumnSettingsDialog', value: boolean): void
  (e: 'handleActiveProjectsFilterChange', value: boolean): void
  (e: 'update:isOnHoldFilter', value: boolean): void
  (
    e: 'handleOnHoldFilterChange',
    value: { filterValue: boolean | null, queryValue: 'true' | 'false' | undefined }
  ): void
  (e: 'update:isReleasedFilter', value: true | null): void
  (e: 'handleReleasedFilterChange', value: true | null): void
  (e: 'update:hiddenVisibilityFilter', value: boolean): void
  (e: 'handleHiddenVisibilityFilterChange', value: boolean): void
  (e: 'update:rejectedItemsOnlyFilter', value: true | null): void
  (e: 'handleRejectedItemsOnlyFilterChange', value: true | null): void
  (e: 'onUpdateTableOptions', value: TableOptions): void
}

const computedLineItems = computed<any[]>({
  // getter
  get() {
    return props.lineItems
  },
  // setter
  set(newValue: any[]) {
    emit('update:lineItems', newValue)
  }
})

const computedSelectedLineItemIds = computed<any[]>({
  // getter
  get() {
    return props.selectedLineItemIds
  },
  // setter
  set(newValue: any[]) {
    emit('update:selectedLineItemIds', newValue)
  }
})

const computedLineItemTableOptions = computed<TableOptions>({
  // getter
  get() {
    return props.lineItemTableOptions
  },
  // setter
  set(newValue: TableOptions) {
    emit('update:lineItemTableOptions', newValue)
  }
})

const tableItemSelectable = computed(() => {
  if (props.isMultipleApprovalDialog) {
    return 'is_can_user_approve'
  }
  return true
})

const emit = defineEmits<Emit>()

const snackbarStore = useSnackbarStore()

// const
const {
  getPriorityColor
} = usePriority()
const itemsPerPageOptions = [
  { value: 15, title: '15' },
  { value: 20, title: '20' },
  { value: 50, title: '50' },
  { value: 100, title: '100' },
]

const getProjectViewLink = (projectId: number) => {
  const pathMap: Record<number, string> = {
    1: 'signs', // Wayfinding
    5: 'kop' // KOP
  }

  // Default - Branding
  const path = (props.campaignType && pathMap[props.campaignType]) || 'branding'
  return { path: `/projects/${path}/${projectId}` }
}

const getResponsibleDisplayName = (responsible: any) => {
  const fullName = [responsible?.first_name, responsible?.last_name]
    .filter(Boolean)
    .join(' ')
    .trim()

  return fullName || responsible?.email || '-'
}

const getLineItemIdentity = (item: any) => {
  const source = (item?.file_name || item?.brand_book_code || '').toString().trim()
  if (!source) return ''
  const dotIndex = source.lastIndexOf('.')
  return dotIndex > 0 ? source.slice(0, dotIndex).trim() : source
}

const downloadSingleFile = async (item: any, fileType: 'artwork' | 'prepress_proof' | 'scope' | 'installation') => {
  const identity = getLineItemIdentity(item)
  if (!item?.campaign || !identity) {
    snackbarStore.showMessage({
      color: 'warning',
      icon: 'mdi-alert-circle',
      title: 'Warning',
      text: 'No file identity available'
    })
    return
  }

  try {
    const result = await downloadFromS3(item.campaign, identity, fileType)
    if (result.status === 'complete') {
      snackbarStore.showMessage({
        color: 'success',
        icon: 'mdi-checkbox-marked-circle',
        title: 'Download Complete',
        text: `All ${result.successCount} file(s) downloaded successfully!`
      })
    } else {
      snackbarStore.showMessage({
        color: 'error',
        icon: 'mdi-alert-circle',
        title: 'Download Failed',
        text: 'Failed to process download request'
      })
    }
  } catch (err: any) {
    console.log('downloadSingleFile error:', err)
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Download Failed',
      text: 'Unexpected error while downloading'
    })
  }
}

const updateSelectedLineItemsLoadingState = (isLoading: boolean) => {
  computedLineItems.value = computedLineItems.value.map(lineItem => ({
    ...lineItem,
    lineItemLoading: computedSelectedLineItemIds.value.includes(lineItem.id) ? isLoading : lineItem.lineItemLoading
  }))
}

const handleSelectedLineItemIdsUpdate = (newSelectedLineItemIds: number[]) => {
  emit('update:selectedLineItemIds', newSelectedLineItemIds)
}

const handleOpenLineItemChat = (lineItemId: number) => {
  const isBulkSelection = computedSelectedLineItemIds.value.length > 1
    && computedSelectedLineItemIds.value.includes(lineItemId)

  emit('update:selectedChatLineItemId', isBulkSelection ? computedSelectedLineItemIds.value : lineItemId)
  emit('update:showGlobalChatDialog', true)
}
</script>

<template>
  <DivTable
    :model-value="selectedLineItemIds"
    :page="lineItemTableOptions.page"
    :items="lineItems"
    :items-length="lineItemsCount"
    :headers="lineItemHeaders"
    :loading="lineItemsLoading"
    :item-selectable="tableItemSelectable"
    grid-id="global-search-line-items-table"
    grid-storage-id="line-items-table"
    no-data-text="Line Items not found"
    class="line-items-table"
    item-value="id"
    show-select
    hover
    tableClass="small-font"
    :items-per-page-options="itemsPerPageOptions"
    :items-per-page="lineItemTableOptions.itemsPerPage"
    :sort-by="(lineItemTableOptions.sortBy as any)"
    @update:options="newValue => emit('onUpdateTableOptions', newValue)"
    @update:modelValue="newValue => handleSelectedLineItemIdsUpdate(newValue)"
    @update:page="newValue => emit('update:lineItemTableOptions', {
      ...lineItemTableOptions,
      page: newValue
    })"
  >
    <template #top>
      <div class="d-flex justify-space-between align-center flex-wrap">
        <VChip>
          Selected {{ selectedLineItemIds.length }} line item(s)
        </VChip>
        <div class="d-flex align-center flex-wrap">
          <div class="d-flex align-center flex-wrap">
            <div
              v-if="showActiveProjectsFilter"
              class="line-item-active-projects-block d-flex align-center flex-wrap mr-1"
            >
              <label class="d-flex align-center">
                Active projects:
              </label>
              <VChip
                :color="isActiveProjectsFilter ? 'primary' : 'secondary'"
                :disabled="isActiveProjectsFilter"
                class="ml-1"
                @click="emit('handleActiveProjectsFilterChange', true)"
              >
                Yes
              </VChip>
              <VChip
                :color="!isActiveProjectsFilter ? 'primary' : 'secondary'"
                :disabled="!isActiveProjectsFilter"
                class="ml-1"
                @click="emit('handleActiveProjectsFilterChange', false)"
              >
                No
              </VChip>
            </div>
            <div class="line-item-on-hold-block d-flex align-center flex-wrap mr-1">
              <label class="d-flex align-center">
                On Hold:
              </label>
              <VChip
                :color="isOnHoldFilter === null ? 'primary' : 'secondary'"
                :disabled="isOnHoldFilter === null"
                class="ml-1"
                @click="emit('handleOnHoldFilterChange', { filterValue: null, queryValue: undefined })"
              >
                All
              </VChip>
              <VChip
                :color="isOnHoldFilter === true ? 'primary' : 'secondary'"
                :disabled="isOnHoldFilter === true || (isOnHoldOptions?.length && !isOnHoldOptions?.includes(true))"
                class="ml-1"
                :class="{ 'text-decoration-line-through' : (isOnHoldOptions?.length && !isOnHoldOptions?.includes(true)) }"
                @click="emit('handleOnHoldFilterChange', { filterValue: true, queryValue: 'true' })"
              >
                Yes
              </VChip>
              <VChip
                :color="isOnHoldFilter === false ? 'primary' : 'secondary'"
                :disabled="isOnHoldFilter === false || (isOnHoldOptions?.length && !isOnHoldOptions?.includes(false))"
                class="ml-1"
                :class="{ 'text-decoration-line-through' : (isOnHoldOptions?.length && !isOnHoldOptions?.includes(false)) }"
                @click="emit('handleOnHoldFilterChange', { filterValue: false, queryValue: 'false' })"
              >
                No
              </VChip>
            </div>
            <div
              class="line-item-rejected-items-only-filter-block d-flex align-center mr-1"
            >
              <VCheckbox
                :model-value="rejectedItemsOnlyFilter"
                :false-value="null"
                label="Show Rejected Items"
                @update:modelValue="newValue => emit('handleRejectedItemsOnlyFilterChange', newValue)"
              />
            </div>
            <div
              class="line-item-released-filter-block d-flex align-center mr-1"
            >
              <VCheckbox
                :model-value="isReleasedFilter"
                :false-value="null"
                label="Show Released Items"
                @update:modelValue="newValue => emit('handleReleasedFilterChange', newValue)"
              />
            </div>
            <div
              v-if="can('view','hiddenlineitem')"
              class="line-item-hidden-visibility-filter-block d-flex align-center mr-1"
            >
              <VCheckbox
                :model-value="hiddenVisibilityFilter"
                :false-value="false"
                label="Show Hidden Items"
                @update:modelValue="newValue => emit('handleHiddenVisibilityFilterChange', newValue)"
              />
            </div>
          </div>
          <VBtn
            v-if="!hideTableColumnSettings"
            variant="elevated"
            density="compact"
            color="secondary"
            icon="mdi-wrench"
            class="rounded-lg"
            @click="emit('update:showTableColumnSettingsDialog', true)"
          >
            <VIcon icon="mdi-wrench" />
            <VMenu
              activator="parent"
              location="top center"
              open-on-hover
              open-delay="100"
              close-delay="0"
              offset="5"
              class="default-tooltip"
            >
              <span class="px-2">
                Table Column Settings
              </span>
            </VMenu>
          </VBtn>
        </div>
      </div>
    </template>
    <template #item.action="{ item }">
      <div class="position-relative d-flex align-center justify-center">
        <VBtn
          size="small"
          variant="elevated"
          density="compact"
          icon="tabler-history-toggle"
          class="rounded-lg ml-1"
          @click="emit('update:selectedHistoryLineItemId', item.raw?.id); emit('update:showLineItemStatusHistoryDialog', true)"
        >
          <VIcon icon="tabler-history-toggle" />
          <VMenu
            activator="parent"
            location="top center"
            open-on-hover
            open-delay="100"
            close-delay="0"
            offset="5"
            class="default-tooltip"
          >
            <span>
              Show line item status history
            </span>
          </VMenu>
        </VBtn>
        <VBtn
          size="small"
          variant="elevated"
          density="compact"
          icon="tabler-bubble-text"
          class="rounded-lg ml-1"
          @click="handleOpenLineItemChat(item.raw?.id)"
        >
          <VIcon icon="tabler-bubble-text" />
          <VMenu
            activator="parent"
            location="top center"
            open-on-hover
            open-delay="100"
            close-delay="0"
            offset="5"
            class="default-tooltip"
          >
            <span v-if="selectedLineItemIds?.length > 1 && selectedLineItemIds.includes(item.raw?.id)">
              Add note to {{ selectedLineItemIds.length }} selected item(s)
            </span>
            <span v-else>
              Show item chat
            </span>
          </VMenu>
        </VBtn>
        <VOverlay
          :model-value="item.raw?.lineItemLoading"
          persistent
          absolute
          contained
          class="align-center justify-center"
        >
          <VProgressCircular
            size="x-small"
            :width="3"
            indeterminate
            color="primary"
          />
        </VOverlay>
      </div>
    </template>
    <template #item.work_order__campaign__name="{ item }">
      <div>
        <RouterLink
          title="Open Project"
          :to="getProjectViewLink(item?.raw?.campaign)"
          class="project-link"
          v-text="item?.raw?.campaign_name"
        />
      </div>
    </template>
    <template #item.work_order__campaign__suppliers="{ item }">
      <span>
        {{ item?.raw?.campaign_suppliers_names }}
      </span>
    </template>
    <template #item.work_order__order_no="{ item }">
      <span>
        {{ item?.raw?.work_order_no }}
      </span>
    </template>
    <template #item.work_order_type_display="{ item }">
      <span>
        {{ item?.raw?.work_order_type_display }}
      </span>
    </template>
    <template #item.item_status__name="{ item }">
      <span
        :class="{'rejected-back-status-color': !!item?.raw?.is_rejected_back_status}"
      >
        {{ item?.raw?.item_status_info?.line_item_status_name }}
      </span>
    </template>
    <template #item.price="{ item }">
      <div class="d-flex align-center">
        <div
          v-if="hiddenVisibilityFilter"
          class="d-flex justify-start align-center"
        >
          <VIcon
            size="small"
            :icon="item?.raw?.visibility === 2 ? 'tabler-coin' : 'tabler-coin-off'"
          />
          <VMenu
            activator="parent"
            location="top center"
            open-on-hover
            open-delay="100"
            close-delay="0"
            offset="1"
            class="default-tooltip"
          >
            <span class="px-2">
              {{ item?.raw?.visibility === 2 ? 'Included in Finances' : 'Excluded from Finances' }}
            </span>
          </VMenu>
        </div>
        {{ formatDecimalAndNumberWithComma(item?.raw?.price) }}
      </div>
    </template>
    <template #item.total="{ item }">
      <div class="d-flex align-center">
        <div
          v-if="hiddenVisibilityFilter"
          class="d-flex justify-start align-center"
        >
          <VIcon
            size="small"
            :icon="item?.raw?.visibility === 2 ? 'tabler-coin' : 'tabler-coin-off'"
          />
          <VMenu
            activator="parent"
            location="top center"
            open-on-hover
            open-delay="100"
            close-delay="0"
            offset="1"
            class="default-tooltip"
          >
            <span class="px-2">
              {{ item?.raw?.visibility === 2 ? 'Included in Finances' : 'Excluded from Finances' }}
            </span>
          </VMenu>
        </div>
        {{ formatDecimalAndNumberWithComma(item?.raw?.total) }}
      </div>
    </template>
    <template #item.responsible="{ item }">
      <div>
        <template v-if="item?.raw?.responsible_list?.length">
          <span
            v-for="(responsible, responsibleKey) in item.raw.responsible_list"
            :key="`responsible-${responsibleKey}`"
          >
            {{ getResponsibleDisplayName(responsible) }}<br>
          </span>
        </template>
        <template v-else>
          {{ item?.raw?.responsible_name }}
        </template>
      </div>
    </template>
    <template #item.assigned_to="{ item }">
      <span>
        {{ item?.raw?.assigned_to_name }}
      </span>
    </template>
    <template #item.description="{ item }">
      <div class="position-relative">
        <VMenu
          v-if="isTextTruncated(item.raw?.description || '')"
          activator="parent"
          location="top"
          open-on-hover
          open-delay="100"
          close-delay="0"
          offset="5"
          class="default-tooltip"
        >
          <span style="max-width: 300px; padding: 10px" class="text-pre-wrap">
            {{ item.raw?.description || '' }}
          </span>
        </VMenu>
        <span class="text-pre-wrap multiline-clamp-4">
          {{ item.raw?.description || '' }}
        </span>
      </div>
    </template>
    <template #item.wo_number="{ item }">
      <span>
        {{ item?.raw?.wo_number || '' }}
      </span>
    </template>
    <template #item.brand_book_code="{ item }">
      <span>
        {{ item?.raw?.brand_book_code || '' }}
      </span>
    </template>
    <template #item.cr_code_ext="{ item }">
      <span>
        {{ item?.raw?.cr_code_ext || '' }}
      </span>
    </template>
    <template #item.cr_code_int="{ item }">
      <span>
        {{ item?.raw?.cr_code_int || '' }}
      </span>
    </template>
    <template #item.supplier_erp="{ item }">
      <span>
        {{ item?.raw?.supplier_erp || '' }}
      </span>
    </template>
    <template #item.ext_ref_num="{ item }">
      <div class="position-relative">
        <VMenu
          v-if="isTextTruncated(item.raw?.ext_ref_num || '')"
          activator="parent"
          location="top"
          open-on-hover
          open-delay="100"
          close-delay="0"
          offset="5"
          class="default-tooltip"
        >
          <span style="max-width: 300px; padding: 10px" class="text-pre-wrap">
            {{ item.raw?.ext_ref_num || '' }}
          </span>
        </VMenu>
        <span class="text-pre-wrap multiline-clamp-4">
          {{ item.raw?.ext_ref_num || '' }}
        </span>
      </div>
    </template>
    <template #item.priority="{ item }">
      <div>
        <VChip
          :color="getPriorityColor(item.raw.priority)"
        >
          {{ item?.raw?.priority_display }}
        </VChip>
      </div>
    </template>
    <template #item.is_on_hold="{ item }">
      <div>
        <VChip
          :color="item?.raw?.is_on_hold ? 'warning' : 'secondary'"
        >
          {{ item?.raw?.is_on_hold ? 'Yes' : 'No' }}
        </VChip>
      </div>
    </template>
    <template #item.artwork_file_updated_at="{ item }">
      <span>
        {{ item.raw.artwork_file_updated_at }}
      </span>
    </template>
    <template #item.artwork_file="{ item }">
      <div class="text-center">
        <a
          v-if="item.raw.artwork_file && !item.raw.artwork_file?.is_archived"
          href="#"
          @click.prevent="downloadSingleFile(item.raw, 'artwork')"
        >
          <VIcon icon="tabler-download" />
        </a>
        <VIcon
          v-else-if="item.raw.artwork_file?.is_archived"
          icon="tabler-archive"
          color="warning"
        />
      </div>
    </template>
    <template #item.prepress_proof_file_updated_at="{ item }">
      <span>
        {{ item.raw.prepress_proof_file_updated_at }}
      </span>
    </template>
    <template #item.prepress_proof_file="{ item }">
      <div class="text-center">
        <a
          v-if="item.raw.prepress_proof_file && !item.raw.prepress_proof_file?.is_archived"
          href="#"
          @click.prevent="downloadSingleFile(item.raw, 'prepress_proof')"
        >
          <VIcon icon="tabler-download" />
        </a>
        <VIcon
          v-else-if="item.raw.prepress_proof_file?.is_archived"
          icon="tabler-archive"
          color="warning"
        />
      </div>
    </template>
    <template #item.scope_file_updated_at="{ item }">
      <span>
        {{ item.raw.scope_file_updated_at }}
      </span>
    </template>
    <template #item.scope_file="{ item }">
      <div class="text-center">
        <a
          v-if="item.raw.scope_file && !item.raw.scope_file?.is_archived"
          href="#"
          @click.prevent="downloadSingleFile(item.raw, 'scope')"
        >
          <VIcon icon="tabler-download" />
        </a>
        <VIcon
          v-else-if="item.raw.scope_file?.is_archived"
          icon="tabler-archive"
          color="warning"
        />
      </div>
    </template>
    <template #item.installation_photos_file_updated_at="{ item }">
      <span>
        {{ item.raw.installation_photos_file_updated_at }}
      </span>
    </template>
    <template #item.installation_photos_file="{ item }">
      <div class="text-center">
        <a
          v-if="item.raw.installation_photos_file && !item.raw.installation_photos_file?.is_archived"
          href="#"
          @click.prevent="downloadSingleFile(item.raw, 'installation')"
        >
          <VIcon icon="tabler-download" />
        </a>
        <VIcon
          v-else-if="item.raw.installation_photos_file?.is_archived"
          icon="tabler-archive"
          color="warning"
        />
      </div>
    </template>
    <template #item.view="{ item }">
      <div class="position-relative">
        <VBtn
          v-if="isShowItemReviewDialogInsteadOfReviewPage"
          size="small"
          @click="
                    emit('update:selectedLineItemForReviewDialog', {
                      campaign_id: item.raw.campaign_id,
                      work_order_id: item.raw.work_order_id,
                      id: item.raw.id
                    });
                    emit('update:showLineItemReviewDialog', true)
                "
        >
          View
        </VBtn>
        <VBtn
          v-else
          size="x-small"
          rounded="sm"
          class="text-decoration-none"
          :to="getLineItemReviewLink(item.raw.id)"
        >
          View
        </VBtn>
        <VOverlay
          v-model="item.raw.lineItemLoading"
          persistent
          absolute
          contained
          class="align-center justify-center"
        >
          <VProgressCircular
            indeterminate
            size="x-small"
            :width="3"
            color="primary"
          />
        </VOverlay>
      </div>
    </template>
  </DivTable>
</template>

<style lang="scss">
.line-items-table {
  table {
    thead > tr > th {
      font-size: 10px;
      border-right: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
      padding: 0 10px;
    }
    tbody {
      font-size: 10px;
      tr > td {
        padding: 0 10px;
      }
    }
    .rejected-back-status-color {
      color: rgb(var(--v-theme-primary));
    }
  }
  .project-link {
    color: rgb(var(--v-theme-primary));
  }

  .multiline-clamp-4 {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 4;
    line-clamp: 4;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: pre-wrap;
  }

  .line-item-on-hold-block {
    .v-chip--disabled {
      opacity: 1;
    }
  }

  .line-item-active-projects-block {
    .v-chip--disabled {
      opacity: 1;
    }
  }

  a {
    color: rgb(var(--v-theme-secondary));
    &:link {
      color: rgb(var(--v-theme-primary));
      &:hover {
        text-decoration: underline;
      }
    }
  }
}
</style>
